import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { transactionAPI, paymentAPI } from '../services/api';

const MyOrders = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    filterOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, orders]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await transactionAPI.getTransactions({ role: 'buyer' });
      const orderData = response.data.data || [];
      setOrders(orderData);
      setFilteredOrders(orderData);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    if (statusFilter === 'all') {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(orders.filter((order) => order.status === statusFilter));
    }
  };

  const handleConfirmDelivery = async (e, orderId) => {
    e.stopPropagation();

    if (!window.confirm('Have you received the item in good condition?')) {
      return;
    }

    try {
      await paymentAPI.confirmDelivery(orderId);
      alert('Delivery confirmed! Payment has been released to the seller.');
      // Refresh orders
      fetchOrders();
    } catch (error) {
      console.error('Failed to confirm delivery:', error);
      alert(error.response?.data?.error || 'Failed to confirm delivery. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      paid: { text: 'Paid', color: '#2196F3', icon: '💳' },
      shipped: { text: 'Shipped', color: '#FF9800', icon: '📦' },
      delivered: { text: 'Delivered', color: '#9C27B0', icon: '🚚' },
      completed: { text: 'Completed', color: '#4CAF50', icon: '✅' },
      disputed: { text: 'Disputed', color: '#f44336', icon: '⚠️' },
    };

    const statusInfo = statusMap[status] || { text: status, color: '#757575', icon: '❓' };

    return (
      <span
        style={{
          backgroundColor: statusInfo.color,
          color: 'white',
          padding: '6px 14px',
          borderRadius: '12px',
          fontSize: '13px',
          fontWeight: '600',
          display: 'inline-block',
        }}
      >
        {statusInfo.icon} {statusInfo.text}
      </span>
    );
  };

  const getActionButton = (order) => {
    if (order.status === 'shipped' && order.purchaseType === 'shipping') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
          <button
            onClick={(e) => handleConfirmDelivery(e, order._id)}
            className="btn btn-success btn-sm"
            style={{ fontWeight: '600', fontSize: '13px', padding: '6px 12px', whiteSpace: 'nowrap' }}
          >
            Confirm Delivery
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/transactions/${order._id}`);
            }}
            className="btn btn-outline-secondary btn-sm"
            style={{ fontSize: '12px', padding: '4px 10px' }}
          >
            View Details
          </button>
        </div>
      );
    }

    if (order.status === 'paid' && order.purchaseType === 'in-person') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
          <button
            onClick={(e) => handleConfirmDelivery(e, order._id)}
            className="btn btn-success btn-sm"
            style={{ fontWeight: '600', fontSize: '13px', padding: '6px 12px', whiteSpace: 'nowrap' }}
          >
            Confirm Pickup
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/transactions/${order._id}`);
            }}
            className="btn btn-outline-secondary btn-sm"
            style={{ fontSize: '12px', padding: '4px 10px' }}
          >
            View Details
          </button>
        </div>
      );
    }

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/transactions/${order._id}`);
        }}
        className="btn btn-outline-primary btn-sm"
        style={{ fontSize: '13px', padding: '6px 12px' }}
      >
        View Details
      </button>
    );
  };

  const getDaysRemaining = (autoReleaseDate) => {
    if (!autoReleaseDate) return null;
    const now = new Date();
    const release = new Date(autoReleaseDate);
    const diff = Math.ceil((release - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getOrderSummary = () => {
    const total = orders.length;
    const pending = orders.filter((o) => o.status === 'paid' || o.status === 'shipped').length;
    const completed = orders.filter((o) => o.status === 'completed').length;
    const totalSpent = orders
      .filter((o) => o.status === 'completed')
      .reduce((sum, o) => sum + (o.amount || 0), 0);

    return { total, pending, completed, totalSpent };
  };

  const summary = getOrderSummary();

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px', paddingBottom: '60px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '10px' }}>
          My Orders
        </h1>
        <p className="text-muted">Track and manage your purchases</p>
      </div>

      {/* Summary Stats */}
      <div className="row" style={{ marginBottom: '30px' }}>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div className="card-body">
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                Total Orders
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#2196F3' }}>
                {summary.total}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div className="card-body">
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                Pending
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#FF9800' }}>
                {summary.pending}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div className="card-body">
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                Completed
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#4CAF50' }}>
                {summary.completed}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div className="card-body">
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                Total Spent
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#9C27B0' }}>
                ${(summary.totalSpent / 100).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="card" style={{ marginBottom: '20px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div className="card-body">
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setStatusFilter('all')}
              className={`btn ${statusFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              All Orders ({orders.length})
            </button>
            <button
              onClick={() => setStatusFilter('paid')}
              className={`btn ${statusFilter === 'paid' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              Paid ({orders.filter((o) => o.status === 'paid').length})
            </button>
            <button
              onClick={() => setStatusFilter('shipped')}
              className={`btn ${statusFilter === 'shipped' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              Shipped ({orders.filter((o) => o.status === 'shipped').length})
            </button>
            <button
              onClick={() => setStatusFilter('delivered')}
              className={`btn ${statusFilter === 'delivered' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              Delivered ({orders.filter((o) => o.status === 'delivered').length})
            </button>
            <button
              onClick={() => setStatusFilter('completed')}
              className={`btn ${statusFilter === 'completed' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              Completed ({orders.filter((o) => o.status === 'completed').length})
            </button>
          </div>
        </div>
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div
          className="card"
          style={{
            border: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textAlign: 'center',
            padding: '60px 20px',
          }}
        >
          <h3 style={{ color: '#999', marginBottom: '20px' }}>
            {statusFilter === 'all' ? 'No orders yet' : `No ${statusFilter} orders`}
          </h3>
          <p className="text-muted">Start shopping to see your orders here</p>
          <button
            onClick={() => navigate('/')}
            className="btn btn-primary"
            style={{ marginTop: '20px' }}
          >
            Browse Items
          </button>
        </div>
      ) : (
        <div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div className="card-body" style={{ padding: 0 }}>
            {filteredOrders.map((order, index) => {
              const daysRemaining = getDaysRemaining(order.autoReleaseDate);
              const needsAction =
                (order.status === 'shipped' && order.purchaseType === 'shipping') ||
                (order.status === 'paid' && order.purchaseType === 'in-person');

              return (
                <div
                  key={order._id}
                  style={{
                    borderBottom: index < filteredOrders.length - 1 ? '1px solid #e0e0e0' : 'none',
                    padding: '20px',
                    transition: 'background-color 0.2s',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                  onClick={() => navigate(`/transactions/${order._id}`)}
                >
                  {/* Action Alert */}
                  {needsAction && (
                    <div
                      style={{
                        backgroundColor: '#fff3cd',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '15px',
                        border: '1px solid #ffc107',
                      }}
                    >
                      <p style={{ margin: 0, fontSize: '14px', color: '#856404', fontWeight: '600' }}>
                        ⚠️ Action Required: Please confirm{' '}
                        {order.purchaseType === 'shipping' ? 'delivery' : 'pickup'} once you receive
                        the item
                      </p>
                    </div>
                  )}

                  <div className="row align-items-center">
                    {/* Item Image */}
                    <div className="col-md-2 col-sm-3 mb-2 mb-md-0">
                      <img
                        src={
                          order.item.images?.[0]?.thumbnail ||
                          order.item.images?.[0]?.fullSize ||
                          '/placeholder.png'
                        }
                        alt={order.item.title}
                        style={{
                          width: '100%',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                        }}
                      />
                    </div>

                    {/* Order Details */}
                    <div className="col-md-5 col-sm-9">
                      <h5 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                        {order.item.title}
                      </h5>
                      <p className="text-muted" style={{ fontSize: '14px', marginBottom: '8px' }}>
                        Order ID: <code>{order._id.slice(-8)}</code>
                      </p>
                      <p className="text-muted" style={{ fontSize: '14px', marginBottom: '8px' }}>
                        Seller: {order.seller.name}
                      </p>
                      <p className="text-muted" style={{ fontSize: '14px', margin: 0 }}>
                        Ordered: {formatDate(order.createdAt)}
                      </p>
                    </div>

                    {/* Status & Info */}
                    <div className="col-md-3 col-sm-6 mb-2 mb-md-0">
                      <div style={{ marginBottom: '10px' }}>{getStatusBadge(order.status)}</div>

                      <p style={{ fontSize: '14px', color: '#666', margin: '5px 0' }}>
                        <strong>Type:</strong>{' '}
                        {order.purchaseType === 'shipping' ? '📦 Shipping' : '🤝 In-Person'}
                      </p>

                      {order.trackingNumber && (
                        <p style={{ fontSize: '14px', color: '#666', margin: '5px 0' }}>
                          <strong>Tracking:</strong> {order.trackingNumber}
                        </p>
                      )}

                      {order.fundsHeldInEscrow && daysRemaining !== null && daysRemaining > 0 && (
                        <p style={{ fontSize: '14px', color: '#FF9800', margin: '5px 0' }}>
                          ⏱️ Auto-release in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
                        </p>
                      )}
                    </div>

                    {/* Price & Action */}
                    <div className="col-md-2 col-sm-6 text-md-end">
                      <div
                        style={{
                          fontSize: '24px',
                          fontWeight: '700',
                          color: '#2196F3',
                          marginBottom: '10px',
                        }}
                      >
                        ${(order.amount / 100).toFixed(2)}
                      </div>
                      {getActionButton(order)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;
